import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, EmptyState, useToast } from "./ui";
import type { VisualSelection } from "../services/builder.service";
import { useAdvancedMode } from "../lib/plain-language";
import {
  previewProxyUrl,
  previewService,
  type PreviewState,
  type PreviewStatus,
} from "../services/preview.service";

const TONE: Record<PreviewStatus, "ok" | "warn" | "danger" | "neutral"> = {
  running: "ok",
  starting: "warn",
  failed: "danger",
  stopped: "neutral",
};

const LABEL: Record<PreviewStatus, string> = {
  running: "no ar",
  starting: "preparando",
  failed: "não abriu",
  stopped: "desligado",
};

/**
 * Preview do projeto dentro do Builder.
 * O iframe aponta para o proxy autorizado do backend — nunca direto ao container.
 */
export default function PreviewPanel({
  projectId,
  onSelect,
}: {
  projectId: string;
  onSelect?: (selection: VisualSelection) => void;
}) {
  const toast = useToast();
  const [state, setState] = useState<PreviewState | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const timer = useRef<number | null>(null);
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selection, setSelection] = useState<VisualSelection | null>(null);
  const [advanced] = useAdvancedMode();

  const load = useCallback(async () => {
    try {
      setState(await previewService.get(projectId));
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [projectId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto estiver iniciando, acompanha o health check feito pelo runner.
  useEffect(() => {
    if (state?.preview?.status !== "starting") return;
    timer.current = window.setTimeout(() => void load(), 3000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [state, load]);

  const act = useCallback(
    async (action: "start" | "stop" | "restart") => {
      setBusy(true);
      try {
        await previewService[action](projectId);
        await load();
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [projectId, load, toast],
  );

  // Metadados chegam do preview só quando o projeto roda com a instrumentação
  // de desenvolvimento ativada. Em produção nada é enviado.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as {
        source?: string;
        type?: string;
        payload?: VisualSelection;
      } | null;

      if (!data || data.source !== "okton-inspector" || data.type !== "selected" || !data.payload) {
        return;
      }

      setSelection(data.payload);
      setSelecting(false);
      frame.current?.contentWindow?.postMessage(
        { source: "okton-builder", type: "select-mode-off" },
        "*",
      );
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const toggleSelect = useCallback(() => {
    const next = !selecting;
    setSelecting(next);
    frame.current?.contentWindow?.postMessage(
      { source: "okton-builder", type: next ? "select-mode" : "select-mode-off" },
      "*",
    );
  }, [selecting]);

  const status = state?.preview?.status ?? "stopped";
  const iframeSrc = useMemo(
    () => (state && state.embeddable ? `${previewProxyUrl(state)}?v=${reloadKey}` : null),
    [state, reloadKey],
  );

  return (
    <section className="flex min-h-[16rem] flex-1 flex-col overflow-hidden rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Seu sistema</h2>
          <Badge tone={state?.enabled ? TONE[status] : "neutral"}>
            {state?.enabled ? LABEL[status] : "indisponível"}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void act("start")} disabled={busy || !state?.enabled}>
            Ligar
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void act("stop")}
            disabled={busy || !state?.enabled}
          >
            Desligar
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void act("restart")}
            disabled={busy || !state?.enabled}
          >
            Reiniciar
          </Button>
          <Button
            size="sm"
            variant={selecting ? "primary" : "secondary"}
            onClick={toggleSelect}
            disabled={!iframeSrc}
          >
            {selecting ? "Cancelar" : "Apontar na tela"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setReloadKey((key) => key + 1)}
            disabled={!iframeSrc}
          >
            Atualizar
          </Button>
          {state?.preview?.url && status === "running" && (
            <a
              className="rounded-md border border-line px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
              href={state.preview.url}
              target="_blank"
              rel="noreferrer"
            >
              Abrir em outra aba
            </a>
          )}
        </div>
      </header>

      {selection && (
        <div className="space-y-1 border-b border-line bg-surface-raised px-4 py-3 text-xs text-ink-muted">
          <p className="text-sm font-semibold text-ink">Item selecionado</p>
          {selection.text ? (
            <p className="truncate">“{selection.text}”</p>
          ) : (
            <p>Item sem texto visível.</p>
          )}
          {advanced && (
            <>
              <p>Tipo: &lt;{selection.tag}&gt;</p>
              {selection.classes && <p className="truncate">Classes: {selection.classes}</p>}
              <p>Arquivo provável: {selection.file ?? "não identificado"}</p>
              <p>Componente: {selection.component ?? "não identificado"}</p>
            </>
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={() => onSelect?.(selection)} disabled={!onSelect}>
              Pedir alteração neste item
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setSelection(null)}>
              Limpar
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1">
        {iframeSrc ? (
          <iframe
            key={iframeSrc}
            title="Seu sistema"
            src={iframeSrc}
            ref={frame}
            className="h-full w-full bg-white"
            sandbox="allow-scripts allow-forms allow-same-origin"
          />
        ) : (
          <div className="flex h-full items-center justify-center px-6">
            <EmptyState
              title={
                status === "starting"
                  ? "Preparando a visualização..."
                  : status === "failed"
                    ? "Não conseguimos abrir o seu sistema"
                    : "Seu sistema está desligado"
              }
              description={
                !state?.enabled
                  ? "A visualização está indisponível nesta instalação. Fale com o responsável pelo ambiente."
                  : (state?.preview?.error ??
                    "Clique em Ligar para ver o seu sistema funcionando aqui.")
              }
            />
          </div>
        )}
      </div>
    </section>
  );
}
